import React, { useState, useEffect, useCallback } from "react";
import { adminAPI } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { formatDate } from "../lib/utils";
import { Activity, Clock, User, ChevronLeft, ChevronRight } from "lucide-react";

const EVENT_LABELS = {
  user_signed_up: "Signed up",
  user_logged_in: "Logged in",
  client_created: "Created a client",
  project_created: "Created a project",
  task_created: "Created a task",
  timer_started: "Started a timer",
  timer_stopped: "Stopped a timer",
  time_entry_created: "Created a time entry",
  manual_time_entry_created: "Added a manual time entry",
  invoice_created: "Created an invoice",
  invoice_sent: "Sent an invoice",
  income_added: "Added an income record",
  expense_added: "Added an expense",
  budget_draft_saved: "Saved a budget draft",
  budget_locked: "Locked a budget",
  budget_row_marked_for_expense_tracker: "Marked budget row(s) for expense tracker",
  budget_row_synced_to_expense_tracker: "Synced budget row(s) to expense tracker",
  analytics_viewed: "Viewed analytics",
  suggestions_applied: "Applied budget suggestions",
};

const BEHAVIOR_LABELS = {
  no_activity_after_signup: { label: "No activity after signup", color: "bg-gray-100 text-gray-700" },
  explored_only: { label: "Explored only", color: "bg-yellow-100 text-yellow-700" },
  activated: { label: "Activated", color: "bg-green-100 text-green-700" },
  returning_user: { label: "Returning user", color: "bg-blue-100 text-blue-700" },
};

function formatEventTime(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return isoString;
  }
}

export default function UserActivityModal({ open, onClose, subscriber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const load = useCallback(
    async (p) => {
      if (!subscriber?.user_id) return;
      setLoading(true);
      try {
        const res = await adminAPI.getUserActivity(subscriber.user_id, p, LIMIT);
        setData(res.data);
      } catch (err) {
        console.error("Failed to load user activity:", err);
      } finally {
        setLoading(false);
      }
    },
    [subscriber?.user_id]
  );

  useEffect(() => {
    if (open && subscriber?.user_id) {
      setPage(1);
      load(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscriber?.user_id]);

  const handlePage = (newPage) => {
    setPage(newPage);
    load(newPage);
  };

  const behavior = data ? BEHAVIOR_LABELS[data.behavior_status] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            User Activity
          </DialogTitle>
          <DialogDescription>
            Detailed activity log and usage summary for this user.
          </DialogDescription>
        </DialogHeader>

        {/* User summary card */}
        {subscriber && (
          <div className="rounded-lg border p-4 bg-muted/30 space-y-1 text-sm">
            <div className="flex items-center gap-2 font-semibold text-base">
              <User className="w-4 h-4" />
              {subscriber.user_name || "N/A"}
            </div>
            <div className="text-muted-foreground">{subscriber.user_email || "N/A"}</div>
            {subscriber.business_name && (
              <div className="text-muted-foreground">{subscriber.business_name}</div>
            )}
            <div className="flex flex-wrap gap-4 pt-1">
              <div>
                <span className="text-muted-foreground">Plan: </span>
                <Badge variant="outline">{subscriber.status || "N/A"}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Signed up: </span>
                {subscriber.user_created_at ? formatDate(subscriber.user_created_at) : "N/A"}
              </div>
              <div>
                <span className="text-muted-foreground">Last activity: </span>
                {subscriber.last_activity ? formatDate(subscriber.last_activity) : "None"}
              </div>
              <div>
                <span className="text-muted-foreground">Total events: </span>
                {subscriber.total_activity_count ?? 0}
              </div>
              {behavior && (
                <div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${behavior.color}`}>
                    {behavior.label}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <p className="text-center text-muted-foreground py-4">Loading…</p>}

        {!loading && data && (
          <>
            {/* Engagement metrics — Part 4 */}
            {(data.first_action_time_hours != null || data.last_action_gap_days != null || data.session_depth != null) && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Engagement Metrics</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="rounded border p-2 text-center bg-background">
                    <div className="text-xl font-bold">
                      {data.first_action_time_hours != null
                        ? data.first_action_time_hours < 1
                          ? `${Math.round(data.first_action_time_hours * 60)}m`
                          : `${data.first_action_time_hours}h`
                        : "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">First Action Time</div>
                    <div className="text-xs text-muted-foreground">(signup → first action)</div>
                  </div>
                  <div className="rounded border p-2 text-center bg-background">
                    <div className="text-xl font-bold">
                      {data.last_action_gap_days != null ? `${data.last_action_gap_days}d` : "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">Last Action Gap</div>
                    <div className="text-xs text-muted-foreground">(days since last activity)</div>
                  </div>
                  <div className="rounded border p-2 text-center bg-background">
                    <div className="text-xl font-bold">{data.session_depth ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">Session Depth</div>
                    <div className="text-xs text-muted-foreground">(actions on day 1)</div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick usage summary */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Usage Summary</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Clients created", value: data.summary.clients_created },
                  { label: "Time entries", value: data.summary.time_entries_created },
                  { label: "Invoices", value: data.summary.invoices_created },
                  { label: "Income records", value: data.summary.income_records },
                  { label: "Expense records", value: data.summary.expense_records },
                  { label: "Budget drafts", value: data.summary.budget_drafts_saved },
                  { label: "Budgets locked", value: data.summary.budgets_locked },
                  { label: "Active days", value: data.active_days },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded border p-2 text-center bg-background">
                    <div className="text-xl font-bold">{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity timeline */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Activity Timeline
                <span className="text-muted-foreground font-normal ml-1">({data.total} events)</span>
              </h3>

              {data.logs.length === 0 ? (
                <p className="text-muted-foreground text-sm py-2">No activity recorded yet.</p>
              ) : (
                <ol className="space-y-1">
                  {data.logs.map((log) => (
                    <li key={log.id} className="flex items-start gap-3 text-sm border-b py-1.5 last:border-0">
                      <span className="text-muted-foreground whitespace-nowrap shrink-0 text-xs pt-0.5">
                        {formatEventTime(log.created_at)}
                      </span>
                      <span className="text-foreground">
                        {EVENT_LABELS[log.event_type] || log.event_type}
                        {log.entity_type && log.entity_id && (
                          <span className="text-muted-foreground text-xs ml-1">
                            ({log.entity_type})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              )}

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex items-center justify-between pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => handlePage(page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {data.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.pages}
                    onClick={() => handlePage(page + 1)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
